import { motion } from "framer-motion";
import { Sprout, Award, Users } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function AboutSection() {
  return (
    <section
      id="about"
      className="py-20 md:py-32 bg-[#F9F8F4]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div {...fadeUp} className="lg:col-span-5 order-2 lg:order-1">
            <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-4 block">
              About Us
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1C18] mb-6 tracking-tight"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              Growing Excellence Since Years
            </h2>
            <p className="text-[#4A4F45] text-lg leading-relaxed mb-6">
              Kishan Coconut Nursery is established with the objective of
              supplying healthy and quality hybrid coconut seedlings to
              farmers and growers across Gujarat and beyond.
            </p>
            <p className="text-[#4A4F45] leading-relaxed mb-8">
              The nursery is managed under the supervision of trained personnel
              who have successfully completed official training from{" "}
              <strong>CPCRI (Central Plantation Crops Research Institute)</strong>.
              All nursery activities are carried out following recommended nursery
              management practices to ensure proper growth, vigor, and plant health.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white rounded-2xl border border-[#E5E2D9]">
                <Sprout className="w-6 h-6 text-[#2F5233] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A1C18]">Hybrid</p>
                <p className="text-xs text-[#8C9187]">Seedlings</p>
              </div>
              <div className="text-center p-4 bg-white rounded-2xl border border-[#E5E2D9]">
                <Award className="w-6 h-6 text-[#E07A5F] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A1C18]">NHB</p>
                <p className="text-xs text-[#8C9187]">Registered</p>
              </div>
              <div className="text-center p-4 bg-white rounded-2xl border border-[#E5E2D9]">
                <Users className="w-6 h-6 text-[#81B29A] mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A1C18]">CPCRI</p>
                <p className="text-xs text-[#8C9187]">Trained</p>
              </div>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="lg:col-span-7 order-1 lg:order-2 relative"
          >
            <div className="relative img-zoom rounded-3xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/erk4sjzu_IMG20260202172842.jpg"
                alt="Hybrid Coconut Seedlings at Kishan Coconut Nursery"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              {/* Experience Badge */}
              <div className="absolute bottom-6 left-6 bg-[#2F5233] text-white p-6 rounded-2xl">
                <p
                  className="text-3xl font-bold mb-1"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  Quality
                </p>
                <p className="text-sm text-white/80">Assured Seedlings</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mother Palms Section */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="mt-16"
        >
          <div className="text-center mb-8">
            <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-2 block">
              Our Source
            </span>
            <h3
              className="text-2xl md:text-3xl font-semibold text-[#1A1C18]"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              Mother Palms
            </h3>
            <p className="text-[#4A4F45] mt-2">
              Quality seedlings from healthy, well-maintained mother palms
            </p>
          </div>

          {/* Mother Palms Photo Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <div className="rounded-2xl overflow-hidden img-zoom aspect-square">
              <img
                src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/r27uyrsn_IMG20260220093259.jpg"
                alt="Mother Palm with coconuts"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden img-zoom aspect-square">
              <img
                src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/v7tnacqv_IMG20260219110855.jpg"
                alt="Mother Palm with coconuts"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden img-zoom aspect-square">
              <img
                src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/69x13mid_IMG20260219110043.jpg"
                alt="Full Mother Palm tree"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden img-zoom aspect-square">
              <img
                src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/pr2zf1qz_IMG20260219105927.jpg"
                alt="Mother Palm with coconuts"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden img-zoom aspect-square">
              <img
                src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/o3g2vmsl_IMG20260219105915.jpg"
                alt="Mother Palm with coconuts"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Videos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <video
                controls
                preload="metadata"
                className="w-full max-h-[350px] object-contain bg-black"
                data-testid="mother-palms-video"
              >
                <source
                  src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/mkh54q23_VIDEO_78ce56e2-b781-4c8c-b4b4-72545b91b0a7.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <video
                controls
                preload="metadata"
                className="w-full max-h-[350px] object-contain bg-black"
                data-testid="mother-palms-video-2"
              >
                <source
                  src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/jfgkc26p_VIDEO_28a1bb6b-a8e1-4544-8061-6ae35e8ab0b8.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
